// TODO: This file was created by bulk-decaffeinate.
// Fix any style issues and re-enable lint.
/*
 * decaffeinate suggestions:
 * DS206: Consider reworking classes to avoid initClass
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import $ from 'jquery';
import _ from 'underscore';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import {assertAndContinue} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import * as statusbar from 'components/StatusBar';
import {NumberIDFolder, StringIDFolder} from 'constants/FolderConstants';
import {BioentityAliasInfo, TagSchema} from 'constants/RegistryConstants';
import {User} from 'constants/UserConstants';
import BaseFolderItem, {BaseFolderItemAttributes} from 'folder-items/BaseFolderItem';
import {Tag, ThirdPartyStorageServiceData} from 'folder-items/FileConstants';
import Store from 'lib/Store';
import {Note} from 'notebook/util/NoteUtilConstants';
import {EntryNote} from 'notebook/util/NoteUtilConstants';
import {Optional} from 'util/Types';

// ProxyFileAttributes are not actually expected to contain most of the BaseFolderItemAttributes
// properties, but we need to extend for typing.
export interface ProxyFileAttributes extends BaseFolderItemAttributes {
  openVersionId: number;
  openVersionApiId: string;
  openVersionModifiedAt: string;
  isLatest: boolean;
  dirty: boolean;
  savedVersion: number;
  openVersion: number;
  lockedFromExternalChange: boolean;
  saving: boolean;
  saveSuccess: boolean;
  isAutoSaving: boolean;
}

export type ProxyFileSetOptions = Backbone.ModelSetOptions & {bypassProxyBlock?: boolean};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface ProxyFile<Proxied extends Record<string, any>, S extends Store> {
  get<K extends keyof ProxyFileAttributes>(key: K): ProxyFileAttributes[K];
  set<K extends keyof ProxyFileAttributes>(
    attributeName: K,
    value: ProxyFileAttributes[K],
    options?: ProxyFileSetOptions
  ): ProxyFile<Proxied, S>;
  set(obj: Partial<ProxyFileAttributes>, options?: ProxyFileSetOptions): ProxyFile<Proxied, S>;
}

/*
 * An abstract Backbone FolderItem model backed by a Store.
 *
 * Modern FolderItem states are typically backed by data queried by GQL and extend from ApolloFileState
 * or ApolloFolderItemBase, so ProxyFile is only used for older items like Protein and
 * several different Notebook folder items.
 *
 * Since the main backing data is derived from a store, the Backbone `set` method
 * will error for most attribute keys (unless `bypassProxyBlock` is passed as an option)
 * and other setter methods from BaseFolderItem are generally redefined to just throw an error
 * (although they can be overridden by subclasses to actually write to the backing store).
 *
 * The class is abstract and concrete subclasses must define, at a minimum:
 * - `proxiedField` attribute which is used as a Backbone key for a copy of the proxied data
 * - `load` method to initially populate the Store with the item's data upon opening
 * - `getStore` method which returns the singleton instance of the backing Store
 * - `getState` method which returns an object with a subset of ProxyFileAttributes and a mapping of
 *              the `proxiedField` key to a copy of this item's proxied data
 *
 * Declaring the Proxied generic type as extending from an arbitrary mapping is kind of cheating;
 * in reality, not all proxied data has fields that get accessed like humanId,
 * thirdPartyStorageServiceData, etc, but we need to conform to parent class interfaces.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
abstract class ProxyFile<Proxied extends Record<string, any>, S extends Store> extends BaseFolderItem {
  proxiedField!: string;

  static initClass(): void {
    // @ts-expect-error
    this.prototype.proxiedField = null;
  }

  initialize(): void {
    this.changeListener = this.changeListener.bind(this);
    this.registerListeners();
  }

  abstract load(params: {versionId?: number | null; deferred: JQueryPromise<void>}): void;

  // Should return the mapping {[proxiedField]: Proxied} alongside a subset of ProxyFileAttributes.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  abstract getState(): Partial<ProxyFileAttributes> & Record<string, any>;

  abstract getStore(): Store;

  proxied(): Proxied {
    // @ts-expect-error
    // It's challenging to type this appropriately because `proxiedField` would be generic and we cannot use
    // a generic type as a key in an interface.
    return this.get(this.proxiedField);
  }
  openVersionId(): number {
    return this.get('openVersionId');
  }
  openVersionApiId(): string {
    return this.get('openVersionApiId');
  }
  openVersionModifiedAt(): string {
    return this.get('openVersionModifiedAt');
  }
  isLatest(): boolean {
    return this.get('isLatest');
  }
  isDirty(): boolean {
    return this.get('dirty');
  }

  // Sync with store
  changeListener(): void {
    this.set(this.getState(), {bypassProxyBlock: true});
  }

  registerListeners(): void {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getStore().addChangeListener(this.changeListener);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.on('change', this.updateStatusBar);
  }

  cleanUp(): void {
    this.stopListening();
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getStore().removeChangeListener(this.changeListener);
  }

  // Override to disable automatically updating the status bar.
  usesAutoSave(): boolean {
    return true;
  }

  isUnsaved(): boolean {
    return this.get('savedVersion') !== this.get('openVersion');
  }

  lockedFromExternalChange(): boolean {
    return this.get('lockedFromExternalChange');
  }

  updateStatusBar(): void {
    if (!this.usesAutoSave()) {
      return;
    }
    // Update the statusbar to show syncing status
    const saving = this.get('saving');
    const noError = this.get('saveSuccess');

    if ((this.lockedFromExternalChange() && this.isUnsaved()) || !this.get('isAutoSaving')) {
      statusbar.showOverlayMessage('Unable to save.');
    } else if (saving || this.isUnsaved()) {
      const msg = noError ? 'Syncing...' : 'Retrying sync...';
      statusbar.showOverlayMessage(msg);
    } else {
      statusbar.setTimeoutMessage('Synced.');
    }
  }

  // Required for BaseFolderItem
  name(): string {
    return this.proxied().name;
  }

  folder(): StringIDFolder | NumberIDFolder | undefined {
    return this.proxied().folder;
  }

  setFolder(folder: NumberIDFolder | StringIDFolder): void {
    throw new Error('setFolder not implemented.');
  }

  projectFolderId(): Optional<string> {
    return this.proxied().projectFolderId;
  }

  editURL(): string {
    return this.proxied().editURL;
  }

  initialNote(): Optional<Note> {
    return this.proxied().note;
  }

  permissions(): Permissions {
    if (!assertAndContinue(this.proxied().permissions, 'Expected permissions to exist on proxied')) {
      // @ts-expect-error
      return {};
    }
    return this.proxied().permissions;
  }

  setPermissions(permissions: Permissions): void {
    throw new Error('setPermissions not implemented.');
  }

  allowEdits(): boolean {
    return super.allowEdits() && this.isLatest();
  }

  // note is versioned with file
  allowEditsToNote(): boolean {
    return this.allowEdits();
  }

  displayLabel(): Optional<string> {
    return this.proxied().displayLabel;
  }

  modifiedAt(): string {
    return this.proxied().modified_at;
  }

  createdAt(): string {
    return this.proxied().created_at;
  }

  tags(): Array<Tag> {
    return this.proxied().tags;
  }

  setTags(tags: Array<Tag>): void {
    throw new Error('setTags not implemented.');
  }

  authors(): Array<User> {
    return this.proxied().authors;
  }

  setAuthors(authors: Array<User>): void {
    throw new Error('setAuthors not implemented.');
  }

  tagSchema(): Optional<TagSchema> {
    return this.proxied().tagSchema;
  }

  setTagSchema(tagSchema: TagSchema): void {
    throw new Error('setTagSchema not implemented.');
  }

  initialBioentityAliasInfos(): Optional<Array<BioentityAliasInfo>> {
    return this.proxied().bioentityAliasInfos;
  }

  thirdPartyStorageServiceData(): ThirdPartyStorageServiceData {
    return this.proxied().thirdPartyStorageServiceData;
  }

  humanId(): string {
    return this.proxied().humanId;
  }

  archiveRecord(): ArchiveRecord | null {
    return this.proxied().archiveRecord;
  }

  setArchiveRecord(archiveRecord: ArchiveRecord | null): void {
    throw new Error('setArchiveRecord not implemented.');
  }

  getEntryNote(): EntryNote {
    throw new Error('getEntryNote is only relevant for Entry and StageEntry proxy files');
  }

  // Override Backbone methods for proxy model

  fetch(param?: Backbone.ModelFetchOptions & {versionId?: number | null}): JQueryPromise<void> {
    if (param == null) {
      param = {};
    }
    let {versionId} = param;
    if (versionId == null) {
      versionId = null;
    }
    const deferred: JQueryDeferred<void> = $.Deferred();
    this.load({versionId, deferred});
    return deferred.promise();
  }

  set<K extends keyof ProxyFileAttributes>(
    attributeName: K,
    value: ProxyFileAttributes[K],
    options?: ProxyFileSetOptions
  ): ProxyFile<Proxied, S>;
  set(obj: Partial<ProxyFileAttributes>, options?: ProxyFileSetOptions): ProxyFile<Proxied, S>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  set(key: any, val: any, options?: any): ProxyFile<Proxied, S> {
    // This should only get called in initialize and FileState::initialize
    // Only allows setting [id, fileState, banner] unless bypassProxyBlock is passed.

    let attrs;
    if (key == null) {
      return this;
    }

    if (typeof key === 'object') {
      attrs = key;
      options = val;
    } else {
      // @ts-expect-error
      (attrs = {})[key] = val;
    }

    if (!options) {
      options = {};
    }

    if (!options.bypassProxyBlock) {
      // eslint-disable-next-line no-restricted-syntax
      const badAttrs = _.reject(_.keys(attrs), (key) => ['id', 'fileState', 'banner'].includes(key));
      if (badAttrs.length) {
        throw new Error(`Proxy model had invalid attributes set: ${badAttrs.join(', ')}`);
      }
    }
    // @ts-expect-error
    // eslint-disable-next-line prefer-rest-params
    return super.set(...arguments);
  }
}
ProxyFile.initClass();

export default ProxyFile;
